import * as React from "react";
import {cx}       from "emotion";

import './change-subscription-card.scss';

import {cs}                from "@Common/react/chain-services";
import {consumeContext}    from "@Common/react/context";
import {ConfirmDialog}     from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {formatMoney2}      from "@Common/ui-components/language/format-money";
import {ModalService3}     from "@Common/ui-components/modal-service-3/modal-service-3";
import {parsePhpDate}      from "@Common/utils/date/parse-php-date";

import {TwoOptionModal}    from "@Xpresso/routes/common/two-option-modal/two-option-modal";
import {Banner, SubscribeButton,
    MONTHS_PERIOD_CONVERSION,
    YEARLY_BENEFITS}           from "./subscription-logic";
import {ChangePlanModal}       from "../change-plan-modal/change-plan-modal";

export const ChangeSubscriptionCard = ({activeSubscription, subscriptionModal, subscriptionPlans, advertisedPlan, action}) => cs(
    consumeContext("apis"),
    ["reloadModal", (_, next) => ModalService3({
        xdQaName: "Subscription Details Modal",
        rootNext: next,
        next: (modal) => TwoOptionModal({
            messageLine1: modal.params,
            button1Label: "Refresh",
            button1Action: () => location.reload(),
            button2Label: "Cancel",
            button2Action: () => modal.close()
        })
    })],
    ["manageAction", ({apis, reloadModal}, next) => next(async () => {
        const portal = await apis.next.subscription.getPortal();
        if(portal?.statusCode == "success" && portal?.data?.url) {
            window.location.href = portal.data.url;
        } else if(portal?.message) {
            if(portal?.internalMessage) {
                reloadModal.show(portal.message + " " + portal.internalMessage);

            } else {
                reloadModal.show(portal.message);
            }
        } else {
            reloadModal.show("Something went wrong");
        }
    })],
    ["cancelScheduledChange", ({apis}, next) => next(async () => {
        await apis.next.subscription.cancelScheduledUpdate();
        location.reload();
    })],
    ["confirmCancel", ({}, next) => ConfirmDialog({
        next: (dialog) => next(() => dialog.show({
            message: "Are you sure you want to cancel this scheduled change?",
            actionName: null,
            buttons: {
                confirmLabel: "Confirm",
            },
        })),
    })],
    ["planDetails", ({}, next) => subscriptionPlans &&
        !subscriptionPlans.loading && subscriptionPlans ? (
            next(subscriptionPlans.pricePoints.find(plan => plan.periodMonths === MONTHS_PERIOD_CONVERSION[advertisedPlan]))
        ) : (
            next("Loading...")
        )
    ],
    ["changePlanModal", ({planDetails}, next) => ChangePlanModal({
        action,
        activeSubscription,
        selectedPlan: activeSubscription ? activeSubscription.planType : advertisedPlan,
        planDetails,
        next,
    })],
    ({planDetails, cancelScheduledChange, confirmCancel, changePlanModal, manageAction}) => (
        planDetails && <div className={cx("change-subscription-card change-subscription-card-188f")} xd-qa-type="card" xd-qa-name={advertisedPlan}>
            <div className="card-container">
                <div className="plan-type" xd-qa-type="label" xd-qa-name={advertisedPlan}>
                    {advertisedPlan}
                </div>
                <div className="plan-cost">
                    <span className="price" xd-qa-type="text" xd-qa-name={formatMoney2(planDetails.priceCents / 100)}>
                        {formatMoney2(planDetails.priceCents / 100)}
                    </span>
                    <span className="frequency" xd-qa-type="text" xd-qa-name={MONTHS_PERIOD_CONVERSION[advertisedPlan] === MONTHS_PERIOD_CONVERSION['Monthly'] ? (
                            "/ mo"
                        ) : MONTHS_PERIOD_CONVERSION[advertisedPlan] === MONTHS_PERIOD_CONVERSION['Quarterly'] ? (
                            "/ qrt"
                        ) : (
                            "/ yr"
                        )}>
                        {MONTHS_PERIOD_CONVERSION[advertisedPlan] === MONTHS_PERIOD_CONVERSION['Monthly'] ? (
                            "/ mo"
                        ) : MONTHS_PERIOD_CONVERSION[advertisedPlan] === MONTHS_PERIOD_CONVERSION['Quarterly'] ? (
                            "/ qrt"
                        ) : (
                            "/ yr"
                        )}
                    </span>
                </div>
                <div className="plan-description" xd-qa-type="text" xd-qa-name="Plan Description">
                    {planDetails.description}
                </div>
                <div className="plan-benefits-container">
                    <div className="plan-benefits">
                        <div className="benefit-header"></div>
                        {YEARLY_BENEFITS.map((benefit) => {
                            return !planDetails.benefits.includes(benefit) &&
                                <div className="benefit-container">
                                    <i className="fa-sharp fa fa-check disabled" xd-qa-type="icon" xd-qa-name={"Disabled Check " + benefit}></i>
                                    <div key={benefit}>
                                        <span className={cx("hidden-benefit")} xd-qa-type="text" xd-qa-name={benefit}>{}</span>
                                    </div>
                                </div>
                            })
                        }
                        {[...planDetails.benefits.filter(benefit => YEARLY_BENEFITS.includes(benefit)), ...planDetails.benefits.filter(benefit => !YEARLY_BENEFITS.includes(benefit))].map((benefit) => (
                            // TODO: remove bandaid solution to keep AI-assisted Property Descriptions from showing up in the benefits list
                            benefit !== "AI-assisted Property Descriptions" &&
                            <div className="benefit-container">
                                <i className="fa-sharp fa fa-check" xd-qa-type="icon" xd-qa-name={"Check " + benefit}></i>
                                <div className={cx("benefit", {yearly: isYearlyBenefit({benefit})})} key={benefit} xd-qa-type="text" xd-qa-name={benefit}>
                                    {benefit}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {activeSubscription.status !== "cancelled" && MONTHS_PERIOD_CONVERSION[advertisedPlan] === activeSubscription.periodMonths && <a
                    className="manage-plan"
                    onClick={() => manageAction()}
                    xd-qa-type="link"
                    xd-qa-name="Manage Plan"
                >
                        Manage Plan
                </a>}
                {activeSubscription.idScheduledPlan === planDetails.idPlanType && <a
                    className="manage-plan"
                    onClick={async () => {
                        if(await confirmCancel()) {
                            cancelScheduledChange()
                        }}
                    }
                    xd-qa-type="link"
                    xd-qa-name="Cancel Scheduled Change"
                >
                        Cancel Scheduled Change
                </a>}
                {activeSubscription.status === "cancelled" ? (
                    SubscribeButton({activeSubscription, planDetails, subscriptionModal, changePlanModal, content: "SUBSCRIBE"})
                ) : activeSubscription.idPlanType === planDetails.idPlanType ? (
                    Banner({content: "ACTIVE PLAN", xdQaName: "Active Plan"})
                ) : activeSubscription.idScheduledPlanType ? (
                    Banner({content: "SCHEDULED CHANGE " + parsePhpDate(activeSubscription.dateNextRenewal).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }), xdQaName: "Scheduled Change"})
                ) : (
                    SubscribeButton({activeSubscription, planDetails, subscriptionModal, changePlanModal, content: "SWITCH TO " + advertisedPlan.toUpperCase()})
                )}
            </div>
        </div>
    )
)

function isYearlyBenefit({benefit}) {
    return YEARLY_BENEFITS.includes(benefit)
}